import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock, Link2, X } from "lucide-react";

const pretty = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

/** 1-on-1 guru booking: one member per evening, 7:00 – 8:00 PM. */
export const OneOnOneBooking = () => {
  const [slots, setSlots] = useState([]);
  const [slotLabel, setSlotLabel] = useState("7:00 – 8:00 PM");
  const [mine, setMine] = useState([]);
  const [picked, setPicked] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: m }] = await Promise.all([
        api.get("/yoga/one-on-one/slots", { params: { days: 14 } }),
        api.get("/yoga/one-on-one/mine"),
      ]);
      setSlots(s.days || []);
      setSlotLabel(s.slot_label || slotLabel);
      setMine((m || []).filter((b) => b.status !== "cancelled"));
    } catch {
      /* member-only — the card simply stays empty */
    }
  };
  useEffect(() => { load(); }, []);

  const book = async () => {
    if (!picked) return toast.error("Pick an evening first");
    setBusy(true);
    try {
      await api.post("/yoga/one-on-one", { date: picked, topic });
      const guru = (await api.get("/yoga/one-on-one/mine")).data.find((b) => b.date === picked && b.status !== "cancelled");
      toast.success(guru?.guru_name
        ? `Booked with ${guru.guru_name} on ${pretty(picked)}, ${slotLabel}`
        : `Booked for ${pretty(picked)}, ${slotLabel}`);
      setPicked(""); setTopic("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    try {
      await api.delete(`/yoga/one-on-one/${id}`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="border-border" data-testid="one-on-one-card">
      <CardContent className="p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-2xl flex items-center gap-2"><CalendarDays size={18}/> Book a 1-on-1 with a guru</h3>
            <p className="text-sm text-muted-foreground mt-1">
              One member per evening, every day at <span className="text-foreground">{slotLabel}</span>. Pick a date at least a day ahead.
            </p>
          </div>
          <Badge variant="outline" className="border-secondary/40 text-secondary flex items-center gap-1">
            <Clock size={12}/> {slotLabel}
          </Badge>
        </div>

        {mine.length > 0 && (
          <div className="mt-6 space-y-2" data-testid="my-bookings">
            {mine.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
                <div className="text-sm">
                  <span className="font-medium">{pretty(b.date)}</span>
                  <span className="text-muted-foreground"> · {b.slot}{b.topic ? ` · ${b.topic}` : ""}</span>
                  <div className="text-xs text-muted-foreground mt-0.5" data-testid={`booking-guru-${b.id}`}>
                    {b.guru_name
                      ? <>With <span className="text-foreground">{b.guru_name}</span>{b.guru_qualification ? `, ${b.guru_qualification}` : ""}{b.guru_speciality ? ` · ${b.guru_speciality}` : ""}</>
                      : "Your guru will be assigned shortly"}
                  </div>
                  {b.session_link && (
                    <a
                      href={b.session_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1"
                      data-testid={`booking-link-${b.id}`}
                    >
                      <Link2 size={11} /> Join your session
                    </a>
                  )}
                </div>
                <button onClick={() => cancel(b.id)} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid={`booking-cancel-${b.id}`}>
                  <X size={13}/> Cancel
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mt-6">
          {slots.map((s) => (
            <button
              key={s.date}
              disabled={s.booked && !s.mine}
              onClick={() => setPicked(s.date)}
              className={`rounded-xl border px-2 py-3 text-xs transition-all ${
                picked === s.date
                  ? "bg-primary text-primary-foreground border-primary"
                  : s.booked
                    ? "border-border bg-muted/40 text-muted-foreground cursor-not-allowed line-through"
                    : "border-border hover:border-primary/50"}`}
              data-testid={`slot-${s.date}`}
            >
              {pretty(s.date)}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3 mt-6">
          <div className="flex-1 min-w-[240px]">
            <Label className="overline">What would you like to work on?</Label>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Lower back, breathwork, sleep…"
              className="mt-2 bg-transparent"
              data-testid="booking-topic"
            />
          </div>
          <Button onClick={book} disabled={busy || !picked} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="booking-confirm">
            {busy ? "Booking…" : "Confirm booking"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default OneOnOneBooking;
